import type { NextComponentType } from 'next';
import type { AppInitialProps, AppProps } from 'next/app';
import getConfig from 'next/config';
import React, { useMemo } from 'react';

import type { IAbTestingInfo } from '@sravni/ab-testing-sdk/lib/node';
import { PopupProvider } from '@sravni/cpa-react/lib/Popup';
import { ThemeName } from '@sravni/design-system-theme';
import type { ThemeNames } from '@sravni/design-system-theme';
import type { IDeviceInfo } from '@sravni/koa-utils/lib/middlewares/device';
import type { ICommonHeadProps } from '@sravni/next-common-head';
import { CommonHead } from '@sravni/next-common-head';
import { Notifications } from '@sravni/react-design-system';
import { ConfigProvider, DeviceInfoProvider, ThemeProvider, AbTestingProvider } from '@sravni/react-utils';
import { formatUserData } from '@sravni/utils/lib/auth';

import type { DehydratedState } from '@tanstack/react-query';
import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { MainLayout } from '../components/MainLayout';
import { Seo } from '../components/Seo';
import { ANALYTICS_OPTIONS } from '../constants/analytics';
import { StoreProvider } from '../providers/store';
import type { GlobalState } from '../store';
import { getOrCreateStore } from '../store';
import { setNavigationMenu } from '../store/navigationMenu/reducer';
import { setMetadata } from '../store/reducers/metadata';
import { destroyUser, setAffiliateInfo, setUser } from '../store/reducers/user';

import '@sravni/design-system-theme/lib/globalStyles.css';
import '../styles/global.scss';
import '../styles/fonts.scss';

const runtimeConfig = {
  gatewayUrl: getConfig().publicRuntimeConfig.gatewayUrl,
  publicUrl: getConfig().publicRuntimeConfig.publicUrl,
  environment: getConfig().publicRuntimeConfig.environment,
};

interface IProps extends AppProps {
  realPath: string;
  initialReduxState: GlobalState;
  deviceInfo: DeviceInfo;
  theme: ThemeNames;
  abTestingInfo?: IAbTestingInfo;
}

const MyApp: NextComponentType<App.ReduxAppContext, AppInitialProps, IProps> = (props) => {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            staleTime: 60 * 1000,
          },
        },
      }),
    [],
  );
  const { Component, pageProps, initialReduxState, deviceInfo, theme, abTestingInfo } = props;
  const [mounted, setMounted] = React.useState(false);
  const reduxStore = getOrCreateStore(initialReduxState);
  const { publicRuntimeConfig } = getConfig();
  const isProduction = publicRuntimeConfig.environment === 'production';
  const { user } = reduxStore.getState();
  const userId = user.loggedIn ? user.account.sub : null;
  const analyticsOptions: ICommonHeadProps['analyticsOptions'] = {
    enabled: isProduction,
    userId,
    googleTmId: ANALYTICS_OPTIONS.GTM_ID,
    yandexMetricsOptions: {
      id: ANALYTICS_OPTIONS.YM_ID,
    },
  };

  React.useEffect(() => {
    setMounted(true);

    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { queue } = require('../utils/queue');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const Sentry = require('@sentry/browser');

    if (isProduction) {
      queue.push(() => {
        Sentry.init({
          dsn: publicRuntimeConfig.sentryDSN,
          release: publicRuntimeConfig.release,
          environment: publicRuntimeConfig.environment,
          /** раскомментировать и добавить нужные ошибки для фильтрации */
          // ignoreErrors: [],
        });
      });

      queue.push(() => {
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.register('/service-worker.js').catch(() => {
            // do nothing
          });
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StoreProvider store={reduxStore}>
      <AbTestingProvider value={abTestingInfo}>
        <DeviceInfoProvider initialInfo={deviceInfo}>
          <ThemeProvider initialTheme={theme}>
            <ConfigProvider value={runtimeConfig}>
              <QueryClientProvider client={queryClient}>
                <Hydrate state={(pageProps as { dehydratedState: DehydratedState }).dehydratedState}>
                  <PopupProvider>
                    <CommonHead analyticsOptions={analyticsOptions} />
                    <Seo />
                    <MainLayout>
                      <Component {...pageProps} />
                    </MainLayout>
                    {mounted && <Notifications />}
                  </PopupProvider>
                </Hydrate>
              </QueryClientProvider>
            </ConfigProvider>
          </ThemeProvider>
        </DeviceInfoProvider>
      </AbTestingProvider>
    </StoreProvider>
  );
};

MyApp.getInitialProps = async ({ Component, ctx }: App.ReduxAppContext) => {
  const reduxStore = getOrCreateStore();
  const isServer = typeof ctx.req !== 'undefined';
  ctx.store = reduxStore;

  if (isServer) {
    const user = ctx.req.__USER__;
    if (user) {
      const account = formatUserData(user, ['phone_number', 'email', 'email_verified', 'unconfirmed-email']);
      reduxStore.dispatch(setUser(account));

      const affiliateInfo = ctx.req.__AFFILIATE_INFO__;
      if (affiliateInfo) {
        reduxStore.dispatch(setAffiliateInfo(affiliateInfo));
      }
    } else {
      reduxStore.dispatch(destroyUser());
    }
    if (ctx.req.__SEO__) {
      reduxStore.dispatch(setMetadata(ctx.req.__SEO__));
    }
    if (ctx.req.__NAVIGATION_MENU__) {
      reduxStore.dispatch(setNavigationMenu(ctx.req.__NAVIGATION_MENU__));
    }
  }

  const pageProps = Component.getInitialProps ? await Component.getInitialProps(ctx) : {};
  const deviceInfo: IDeviceInfo =
    isServer && ctx.req.__DEVICE_INFO__
      ? ctx.req.__DEVICE_INFO__
      : { phone: false, tablet: false, isAppleMobile: false };
  const theme: ThemeNames = isServer && ctx.req.__THEME__ ? ctx.req.__THEME__ : ThemeName.lager;
  const abTestingInfo = isServer && ctx.req.__AB_TESTING__ ? ctx.req.__AB_TESTING__ : undefined;

  return {
    initialReduxState: reduxStore.getState(),
    realPath: ctx.req?.url || '',
    pageProps,
    deviceInfo,
    theme,
    abTestingInfo,
  };
};

export default MyApp;
